import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Alert,
} from "react-native";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase.config";
import { getCollectionPath } from "../utils/dbPaths";
import { useAuthStore } from "../stores";
import AuthHeader from "../components/AuthHeader";

const SubmitBugReport = () => {
  const navigate = useNavigate();
  const [bugReport, setBugReport] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  const handleSubmit = async () => {
    if (!bugReport.trim()) {
      Alert.alert("Error", "Please enter a bug report description.");
      return;
    }

    setSubmitting(true);

    try {
      const bugReportPath = getCollectionPath("bugreport");
      const bugReportData = {
        text: bugReport.trim(),
        submittedBy: user?.email || user?.uid || "anonymous",
        submittedAt: serverTimestamp(),
        userId: user?.uid || null,
      };

      await addDoc(collection(db, bugReportPath), bugReportData);

      Alert.alert("Success", "Bug report submitted successfully!", [
        {
          text: "OK",
          onPress: () => {
            setBugReport("");
            navigate(-1);
          },
        },
      ]);
    } catch (error) {
      console.error("Error submitting bug report:", error);
      Alert.alert("Error", "Failed to submit bug report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View style={styles.container}>
      <AuthHeader
        title="Submit Bug Report"
        subtitle=""
        isDesktop={window.innerWidth >= 768}
        isAuthenticated={isAuthenticated}
        shopInfo={{}}
        storeLogo=""
      />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          {/* Thank You Graphic */}
          <View style={styles.thankYouCard}>
            <Text style={styles.thankYouEmoji}>🙏</Text>
            <Text style={styles.thankYouText}>
              Thank you for taking the time to submit a report. Bonita Bikes has commissioned this custom app and appreciates your help in development!
            </Text>
          </View>

          <Text style={styles.label}>Describe the bug or issue:</Text>
          <TextInput
            style={styles.textInput}
            placeholder="Enter bug report details..."
            placeholderTextColor="#8A9794"
            multiline
            numberOfLines={10}
            value={bugReport}
            onChangeText={setBugReport}
            editable={!submitting}
          />

          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.submitButtonText}>Submit Bug Report</Text>
            )}
          </TouchableOpacity>

          {isAuthenticated && (
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.viewReportsButton}
              onPress={() => navigate("/view-bug-reports")}
            >
              <Text style={styles.viewReportsButtonText}>View Bug Reports</Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  content: {
    maxWidth: 800,
    alignSelf: "center",
    width: "100%",
  },
  thankYouCard: {
    backgroundColor: "#E4F4EC",
    borderRadius: 8,
    padding: 20,
    marginBottom: 24,
    alignItems: "center",
    borderWidth: 2,
    borderColor: "#1F8A5B",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  thankYouEmoji: {
    fontSize: 48,
    marginBottom: 12,
  },
  thankYouText: {
    fontSize: 16,
    color: "#1F8A5B",
    textAlign: "center",
    lineHeight: 24,
    fontWeight: "500",
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  textInput: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    minHeight: 200,
    textAlignVertical: "top",
    marginBottom: 16,
  },
  submitButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: "center",
    marginBottom: 16,
  },
  submitButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.6,
  },
  submitButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  viewReportsButton: {
    backgroundColor: "#1F8A5B",
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: "center",
  },
  viewReportsButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default SubmitBugReport;

